// BulkActionBar.jsx
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import RowActionsMenu from './RowActionsMenu'
import {
  FIELD_HEIGHT_SX,
  FS,
  MOBILE_BOTTOM_NAV_CLEARANCE_SX,
  MOBILE_SELECTION_BAR_Z_INDEX,
  SHADOWS,
  SP,
} from '../../theme/tokens'

/**
 * BulkActionBar — die Sammelleiste einer Liste (BS16, G0.9).
 *
 * @description
 *   Zwei Formen derselben Sache, und die Form hängt an der **Breite**, nicht an
 *   der Eingabeart (§11.3):
 *
 *   - `mode="inline"` (Desktop) — die Leiste **ersetzt den Tabellenkopf** an
 *     derselben Stelle und in derselben Höhe: „N selected" · 1–3 Hauptaktionen ·
 *     ⋮ · „Select all M matching" · „Clear selection" (§9.5). Kein schwebender
 *     Balken; der Kopf ist der Platz, an dem die Auswahl entsteht.
 *   - `mode="mobile"` — die Aktionsleiste **unten**, **an der Stelle der
 *     Bottom-Navigation**: „N selected" · 1–3 Hauptaktionen · ⋮ ·
 *     „Select all (n)" · „Cancel" (§11.6).
 *
 *   🔴 **`mode="mobile"` gehört an den Mobil-Zweig (`down('sm')`)** — die Leiste
 *   ersetzt dort die Unterleiste; oberhalb `sm` rendert der Aufrufer die `inline`
 *   Form (R16 W1, 2026-10-12). Der Baustein hängt bewusst **nur** am `mode` und
 *   nicht zusätzlich am Viewport; die Haus-Unterleiste existiert aber **nur**
 *   unterhalb `sm` (`SidebarAndHeaderBar.jsx`, `useMediaQuery(breakpoints.down('sm'))`).
 *   „Die Leiste deckt die Unterleiste ab" ist damit eine Aussage über eine
 *   Koexistenz, die der **Aufrufer** herstellen muss.
 *
 *   🔴 **Mobil liegt die Leiste ÜBER der Haus-Unterleiste UND DECKT SIE AB.** Der
 *   Verlauf von `MobileBottomNav` ist am unteren Anschlag **deckend** und trägt
 *   `pointerEvents: 'none'` — eine Leiste auf `zIndex: appBar` (1100) wäre hinter
 *   ihm unsichtbar und trotzdem klickbar. Der Wert kommt aus
 *   `MOBILE_SELECTION_BAR_Z_INDEX` (über `MOBILE_BOTTOM_NAV_Z_INDEX`, einer
 *   **Abschrift** des Literals `MobileBottomNav.jsx:272`), und der Geräterand
 *   kommt als `pb` mit `env(safe-area-inset-bottom)` dazu — dieselbe Hausform wie
 *   der Rahmen der Unterleiste. `MobileBottomNav.jsx` bleibt dabei **unberührt**.
 *
 *   🔴 **„Deckt sie ab" ist eine Höhenaussage, keine Stapelaussage** (R16 C1,
 *   2026-10-12). Der `zIndex` allein legt die Leiste nur *vor* die Unterleiste —
 *   ein 44 px hoher Balken vor einem Band, dessen Oberkante auf
 *   `env(safe-area-inset-bottom) + 70 px` liegt (14 px Polster + 56 px Band),
 *   lässt dessen oberen Teil hervorschauen: zwei Bedienflächen halb ineinander an
 *   derselben Kante. Deshalb trägt die mobile Form `MOBILE_BOTTOM_NAV_CLEARANCE_SX`
 *   (derselbe Wert, den `main` unten ohnehin freihält) statt der 44 px aus
 *   `FIELD_HEIGHT_SX`, und ihr Inhalt sitzt **senkrecht mittig**.
 *
 *   🔴 **Der Geräterand steht in der HÖHE, nicht nur im `pb`** (R16 Bedingung 2,
 *   2026-10-12). Der Kasten ist `box-sizing: border-box`; mit einer **festen**
 *   Zahl als `min-height` läge der Rand *in* ihr, und die Leiste deckte nur bis
 *   `Rand ≤ 26 px` — auf einem Gerät mit Home-Indikator bliebe ein Streifen des
 *   Bandes stehen. `MOBILE_BOTTOM_NAV_CLEARANCE_SX` trägt deshalb die
 *   **`calc`-Form** (`96 px + env(safe-area-inset-bottom)`) in der Grundregel
 *   **und** im `@media (pointer: coarse)`-Block; der Unterschied gegen die
 *   Bandoberkante ist damit randunabhängig `96 − 70 = 26 px`. Der `pb` bleibt
 *   daneben stehen, wie bei der Unterleiste.
 *
 *   🔴 **Warum ein Token und nicht eine blanke `minHeight`-Zeile:** der Block
 *   muss den Medienblock **mitbringen**. `FIELD_HEIGHT_SX` trägt
 *   `...TOUCH_TARGET_COARSE` (`@media (pointer: coarse) { minHeight: 44 }`), und
 *   emotion emittiert ihn **nach** der Grundregel — eine blanke Zusage daneben
 *   verlöre also auf genau den Geräten, für die sie gedacht ist. Gemessen: mit
 *   einer blanken Zeile stand am Ende `min-height: 96px` **und**
 *   `@media (pointer: coarse) { min-height: 44px }` daneben.
 *
 *   🔴 **Die zweite Auswahlstufe ist sichtbar eine andere.** Ist `modus === 'all'`
 *   („alle passenden außer …"), sagt die Leiste „**All {n} selected**" — nicht
 *   „{n} selected" — und der Link in Stufe 2 wird **nicht** gerendert. Das ist
 *   der Unterschied, den `REFERENCE-PATTERNS.md` §4.1 als *„a safety feature,
 *   not a nicety"* führt: der Nutzer wählt ausdrücklich zwischen „was ich sehe"
 *   und „was ich abgefragt habe", und die Tragweite einer zerstörenden Aktion
 *   wird eine informierte Wahl.
 *
 *   🔴 **Die Leiste erscheint erst ab einer Schwelle.** Ohne Auswahl rendert der
 *   Baustein `null`; die Vorgabe der Schwelle hängt am `mode` — `inline` **2**
 *   (`pattern-library.md`: *„Bulk actions … only when ≥2 items are selectable.
 *   Hidden otherwise."*), `mobile` **1** (der Modus beginnt mit **einer**
 *   markierten Karte und braucht „Cancel" sofort). `minCount` überschreibt sie.
 *
 *   🔴 **Kein `role="status"` auf „N selected".** P8 verlangt **genau einen**
 *   Zähler mit `role="status"` je Ansicht und **keine** weitere Zählung in
 *   Fußzeile oder Leiste — der eine Zähler ist `ResultCount` (BS4). Ein zweiter
 *   Live-Bereich hier ließe jeden Screenreader zwei Zahlen ansagen.
 *
 *   🔴 **`role="group"`, nicht `role="toolbar"`.** Das ARIA-APG-Muster für
 *   `toolbar` verlangt Pfeiltasten-Navigation mit wanderndem `tabindex`; die
 *   Knöpfe hier sind gewöhnlich tabbar, und es gibt keinen Tastatur-Handler. Ein
 *   Screenreader kündigte „Werkzeugleiste" an und erwartete dann Pfeiltasten, die
 *   nichts tun. Der Name bleibt in beiden Fällen Pflicht.
 *
 *   🔴 **Der Start des Modus wird über einen EIGENEN versteckten Live-Text
 *   angesagt** (R16 W1/Bedingung 1, 2026-10-12). Er kann **nicht** über den
 *   Zähler laufen: `ResultCount` (BS4) nimmt keinen Auswahlzustand entgegen, und
 *   das Markieren einer Zeile ändert die **Trefferzahl** nicht — beim Modusstart
 *   änderte also *keine* Live-Region ihren Text. Die Region steht **immer** im DOM
 *   (auch ohne Auswahl, dann leer).
 *
 *   🔴 **Sie folgt der SICHTBARKEIT der Leiste, nicht der Zahl der Markierungen**
 *   (G0.9 R15 R4 W-2, ausgeführt in G0.11): `unsichtbar → sichtbar` sagt
 *   „Selection mode on", `sichtbar → unsichtbar` sagt „Selection mode off". Die
 *   Schwelle hängt am `mode` — `inline` **2**, `mobile` **1**. Eine Ansage, die
 *   einen Zustand **ohne sichtbare Fläche** meldet, ist falsch: bei `inline` mit
 *   EINER markierten Zeile steht keine Leiste, und „Selection mode on" sagte
 *   einen Modus an, den dort niemand sieht. Gemessen wird deshalb derselbe Wert,
 *   an dem die Leiste hängt.
 *
 *   🔴 **Der Aufrufer montiert die Leiste DAUERHAFT** (nicht `{count > 0 && <BulkActionBar/>}`) —
 *   sonst entfällt die Modus-Ansage; **eine Instanz je Liste** (inline ODER mobile), sonst kommt
 *   die Ansage doppelt (R16 R4 W2/W3, 2026-10-12).
 *
 *   🔴 **P8 begrenzt ZÄHLUNGEN, nicht Live-Bereiche.** Wortlaut
 *   (`concepts/tabellen-filter-suche.md:1473`): *„genau ein `role="status"`-Zähler;
 *   Text `^\d… (of \d… )?<noun>$` oder „50+"; N = Trefferzahl; **keine weitere
 *   Zählung** in Fußzeile/Leiste"*. Der Text dieser Region ist keine Trefferzahl
 *   und hat die Zählerform nicht — er fällt deshalb nicht unter P8.
 *   `role="status"` trägt er **nicht**; die Rolle bleibt dem einen Zähler.
 *
 *   🔴 **Keine Zahl im Baustein.** Höhe aus `FIELD_HEIGHT_SX` (die eine Höhe je
 *   Zeile, N2.4), Abstände aus `SP`, Schrift aus `FS`, Schatten aus `SHADOWS`
 *   (Regel 22). Und **kein** rohes `<Chip>` — die Hausform dafür ist `Pill`,
 *   hier reicht Text.
 *
 *   🔴 **Pflichtnamen werfen, statt still zurückzufallen** (Hausform
 *   `EmptyState.reason`, `MobileRecordCard.selectable.label`): `label` ist der
 *   zugängliche Name der Leiste, `moreLabel` der des ⋮-Knopfes, und jede
 *   Aktion trägt einen sichtbaren `label`. Fehlt eines, **wirft** der Baustein.
 *
 * @useWhen
 *   - Eine Liste bietet Sammelaktionen an und `useRowSelection` (BS16) liefert
 *     den Zustand — Desktop als Ersatz des Tabellenkopfs, mobil als Leiste unten
 *   - 1–3 Aktionen sind häufig genug für einen sichtbaren Knopf, der Rest
 *     gehört ins ⋮ (`promotedBulkActions`, Polaris)
 *
 * @dontUseFor
 *   - Eine Zeile mit einer einzigen Aktion — dafür `RowActionsMenu`
 *   - Eine Seitenkopf-Werkzeugleiste (Z1) — das ist `ListToolbar` (BS2), nicht
 *     der Auswahlzustand
 *   - Mehr als drei Hauptaktionen — dann stimmt die Auswahl der Aktionsgruppe,
 *     nicht die Leiste
 *
 * @related
 *   - `useRowSelection` (BS16) — liefert `count`, `modus`, `headerCheckbox`
 *   - `RowActionsMenu` — der ⋮-Knopf der Leiste (kein zweiter Kebab)
 *   - `ResultCount` (BS4) — der EINE `role="status"`-Zähler der Ansicht
 *   - `MobileRecordCard.selectable` (BS12) — die Checkbox der Karte mobil
 *
 * @reuseHints
 *   - Die Musterroute (G0.11) rendert beide Formen; die Ansichtskarten G1–G15
 *     schalten sie mit ihren eigenen Aktionsgruppen ein
 *   - G1 setzt `scope` (Reiter/Filter, z. B. „Unread") und übergibt
 *     `returnFocusRef` auf die Liste, damit „Cancel" den Fokus zurückgibt
 *
 * @props
 *   selection     — Pflicht: was `useRowSelection` liefert (`count`, `modus`,
 *                   `canSelectAllMatching`, `selectAllMatching`, `clear` …)
 *   label         — Pflicht: der zugängliche Name der Leiste (`role="group"`)
 *   actions       — 1–3 `{ label, onClick, disabled?, icon? }` — sichtbare Knöpfe
 *   more          — `{ key?, label, onClick?, danger?, disabled?, icon? }[]` —
 *                   der Rest hinter dem ⋮ (Form wie `RowActionsMenu`)
 *   moreLabel     — Pflicht, sobald `more` Einträge hat: der Name des ⋮-Knopfes
 *   resourceName  — optional: die Ressource für „Select all 137 matching cases";
 *                   ohne sie heißt der Link „Select all 137 matching"
 *   scope         — optional: der **Umfang** der Auswahl als Text (z. B.
 *                   „Unread") — die Leiste sagt dann „7 selected in Unread".
 *                   `concepts/inboxes.md` §2.4(3)/§5.6 führt das als Defekt, wenn
 *                   die Leiste nicht sagt, **worauf** sie wirken wird
 *   mode          — `'inline'` (Vorgabe) | `'mobile'`
 *   minCount      — optional: ab wie vielen markierten Zeilen die Leiste
 *                   erscheint. Vorgabe je `mode`: `inline` 2, `mobile` 1
 *   returnFocusRef — optional: `React.Ref` auf das Element, das den Fokus
 *                   zurückbekommt, wenn die Leiste verschwindet („Cancel", letzte
 *                   Abwahl). Ohne sie fiele der Fokus auf `<body>`
 *   sx            — zuletzt angewandt, der Aufrufer gewinnt
 */
export default function BulkActionBar({
  selection,
  label,
  actions = [],
  more = [],
  moreLabel,
  resourceName,
  scope,
  mode = 'inline',
  minCount,
  returnFocusRef,
  sx,
}) {
  if (!selection || typeof selection.count !== 'number') {
    throw new Error(
      'BulkActionBar: `selection` fehlt — es kommt aus `useRowSelection` (BS16). ' +
        'Ohne es gäbe es keine Auswahl zu zeigen.',
    )
  }
  if (!label) {
    throw new Error(
      'BulkActionBar: `label` fehlt — der zugängliche Name der Leiste (`role="group"`). ' +
        'Ein Rückfallname hieße auf jeder Liste gleich.',
    )
  }
  // Hausform `FilterSheet.withPanel`/`ariaLabel`: der Name wird erst verlangt,
  // wenn die Flaeche wirklich entsteht — ein leeres `more` baut keinen Knopf.
  if (more.length > 0 && !moreLabel) {
    throw new Error(
      'BulkActionBar: `moreLabel` fehlt — der zugängliche Name des ⋮-Knopfes. ' +
        'Pflicht, sobald `more` Einträge hat.',
    )
  }
  // Polaris `promotedBulkActions`: die Leiste trägt 1–3 Hauptaktionen, der Rest
  // gehört ins ⋮. Laut statt still — fünf Knöpfe in einer Zeile sind keine
  // Auswahl mehr, sondern eine Werkzeugleiste.
  if (actions.length > 3) {
    throw new Error(
      `BulkActionBar: ${actions.length} Hauptaktionen — erlaubt sind höchstens 3 (§9.5). ` +
        'Der Rest gehört nach `more` (⋮).',
    )
  }
  actions.forEach((a, i) => {
    if (!a || !a.label) {
      throw new Error(
        `BulkActionBar: die Hauptaktion an Stelle ${i} hat kein \`label\` — ` +
          'ein Knopf ohne sichtbaren Text ist ein Knopf ohne Namen.',
      )
    }
  })

  const mobil = mode === 'mobile'
  // Die Schwelle haengt am `mode` (R16 C4): Desktop erst ab zwei Zeilen
  // (`pattern-library.md`), mobil ab der ersten — der Modus beginnt mit EINER
  // markierten Karte und braucht „Cancel" sofort.
  const schwelle = Number.isFinite(minCount) ? minCount : (mobil ? 1 : 2)
  const sichtbar = selection.count >= schwelle

  // 🔴 Der Fokus darf beim Ausblenden nicht auf `<body>` fallen (R16 W3). Der
  // Aufrufer uebergibt die Karte/die Liste; `useLayoutEffect` raeumt SYNCHRON
  // vor dem Ausblenden auf — ein gewoehnlicher `useEffect` liefe erst nach dem
  // Commit, und der Fokus saesse fuer einen Frame auf `<body>`.
  useLayoutEffect(() => {
    if (!sichtbar) return undefined
    const ziel = returnFocusRef ? returnFocusRef.current : null
    return () => {
      if (ziel && typeof ziel.focus === 'function') ziel.focus()
    }
  }, [sichtbar, returnFocusRef])

  // 🔴 **Esc im mobilen Modus = „Cancel"** (R16 W3). Der Zuhörer liegt auf
  // `document`, nicht am Kasten: die Leiste ist selbst nicht fokussierbar, und
  // der Modus laeuft ueber der ganzen Liste.
  useEffect(() => {
    if (!mobil || !sichtbar) return undefined
    const beiTaste = (e) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      selection.clear()
    }
    document.addEventListener('keydown', beiTaste)
    return () => document.removeEventListener('keydown', beiTaste)
  }, [mobil, sichtbar, selection])

  // 🔴 **Die Modus-Ansage** (R16 W1/Bedingung 1, 2026-10-12). Sie kann NICHT ueber
  // den Zaehler laufen — `ResultCount` (BS4) hat keinen Eingang fuer den
  // Auswahlzustand, und das Markieren einer Zeile aendert die TREFFERZAHL nicht.
  // Ohne eigenen Text aenderte beim Modusstart also keine Live-Region ihren Inhalt.
  // 🔴 P8 begrenzt Zaehlungen, nicht Live-Bereiche
  // (`concepts/tabellen-filter-suche.md:1473`: „genau ein `role=\"status\"`-Zaehler
  // … keine weitere Zaehlung in Fusszeile/Leiste"); dieser Text ist keine
  // Trefferzahl. Er traegt deshalb `aria-live`, aber KEIN `role` — die Rolle
  // gehoert dem einen Zaehler.
  // 🔴 **Die Ansage haengt an der SICHTBARKEIT der Leiste, nicht an
  // `selection.count`** (G0.9 R15 R4 W-2, ausgefuehrt in G0.11). Die Schwelle
  // haengt am `mode` — `inline` 2, `mobile` 1. Eine Ansage, die einen Zustand
  // OHNE sichtbare Flaeche meldet, ist falsch: bei `inline` mit EINER markierten
  // Zeile steht keine Leiste, und „Selection mode on" sagte einen Modus an, den
  // dort niemand sieht. Gemessen wird deshalb derselbe Wert, an dem die Leiste
  // haengt — `sichtbar`.
  const vorherSichtbar = useRef(sichtbar)
  const [modusAnsage, setModusAnsage] = useState('')
  useEffect(() => {
    const vorher = vorherSichtbar.current
    vorherSichtbar.current = sichtbar
    if (!vorher && sichtbar) setModusAnsage('Selection mode on')
    else if (vorher && !sichtbar) setModusAnsage('Selection mode off')
  }, [sichtbar])

  // 🔴 Die leere Leiste gibt es nicht: ohne Auswahl ist der Tabellenkopf der
  // richtige Inhalt an dieser Stelle (§9.5). Die Live-Region bleibt trotzdem
  // stehen — eine Region, die erst MIT ihrem Inhalt eingehaengt wird, ist fuer
  // Screenreader oft keine Aenderung, sondern Erstinhalt, und wird nicht angesagt.
  // Sie ist in beiden Zweigen das ERSTE Kind desselben Fragments, damit React sie
  // nicht neu einhaengt.
  const ansageRegion = (
    <Box
      component="span"
      aria-live="polite"
      data-testid="bulk-action-bar-live"
      sx={visuallyHidden}
    >
      {modusAnsage}
    </Box>
  )
  if (!sichtbar) return <>{ansageRegion}</>

  // 🔴 Stufe 2 nennt sich selbst (R16 B2): „All 137 selected" statt „137
  // selected". Der Zaehler allein liesse „die Seite" und „die Treffermenge"
  // gleich aussehen — genau die Unterscheidung, die das Muster traegt.
  const stufe2 = selection.modus === 'all'
  const zaehlText = stufe2 ? `All ${selection.count} selected` : `${selection.count} selected`
  const umfangText = scope ? `${zaehlText} in ${scope}` : zaehlText
  const stufe2Text = mobil
    ? `Select all (${selection.total ?? 0})`
    : `Select all ${selection.total ?? 0} matching${resourceName ? ` ${resourceName}` : ''}`

  // 🔴 `position: fixed` steht bewusst OHNE Medienabfrage im `sx`: die Form
  // haengt am `mode`, nicht am Viewport — eine zweite Weiche ueber die Breite
  // formte dieselbe Leiste zweimal (§11.3, gleiche Begruendung wie bei
  // `MobileRecordCard`).
  const grund = mobil
    ? {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        // 🔴 UEBER der Unterleiste (`MobileBottomNav.jsx`, `zIndex: 1200`):
        // §11.6 sagt „an der Stelle der Bottom-Navigation", und der Verlauf der
        // Unterleiste ist am unteren Anschlag deckend.
        zIndex: MOBILE_SELECTION_BAR_Z_INDEX,
        // 🔴 Der Geraeterand kommt als Polster dazu — dieselbe Hausform wie
        // `MobileBottomNav` (`pb: 'calc(env(safe-area-inset-bottom) + …)'`).
        // Ohne ihn saessen die Knoepfe im Home-Indikator-Streifen.
        pb: 'env(safe-area-inset-bottom, 0px)',
        // 🔴 `height: 'auto'` UEBER `FIELD_HEIGHT_SX`: dessen `height` ist fest,
        // und mit `box-sizing: border-box` naehme das Polster die Zeile auf einem
        // Geraet mit Home-Indikator auf den Rest zusammen. `minHeight` traegt den
        // Hoehenvertrag weiter — der Kasten WAEchst um den Geraeterand.
        height: 'auto',
        // 🔴 **Die Deckung** (R16 C1/Bedingung 2, 2026-10-12): NICHT die 44 px aus
        // `FIELD_HEIGHT_SX`, sondern das Band, das die Unterleiste belegt — und
        // zwar in der **`calc`-Form** (`96 px + env(safe-area-inset-bottom)`,
        // derselbe Wert, den `main` unten freihält). Bei 44 px schaute das Band
        // (`MobileBottomNav.jsx`, Oberkante `env(safe-area-inset-bottom) + 70 px`)
        // ueber der Leiste hervor; mit einer FESTEN Zahl laege der Geraeterand in
        // der border-box und die Deckung endete bei 26 px Rand. Der Token traegt
        // den Medienblock MIT — eine blanke `minHeight`-Zusage daneben verlöre
        // gegen `FIELD_HEIGHT_SX`'s `@media (pointer: coarse)` (emotion emittiert
        // ihn danach), also genau auf den Geraeten, fuer die sie gedacht ist.
        ...MOBILE_BOTTOM_NAV_CLEARANCE_SX,
        borderTop: '1px solid',
        boxShadow: SHADOWS.panel,
      }
    : {
        borderBottom: '1px solid',
      }

  return (
    <>
      {ansageRegion}
      <Box
        role="group"
        aria-label={label}
        data-testid="bulk-action-bar"
        data-mode={mode}
        data-stufe={stufe2 ? 'all' : 'page'}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: SP.tight,
          px: SP.tight,
          ...FIELD_HEIGHT_SX,
          bgcolor: 'background.paper',
          ...grund,
          // 🔴 NACH dem Shorthand (R16 C1). `borderTop`/`borderBottom` sind
          // Shorthands und setzen die ausgelassene Farbe auf ihren Anfangswert
          // `currentColor` zurueck — stand `borderColor` davor, fiel der Rand auf
          // die Textfarbe (nahezu schwarz) statt auf `divider`. Hausform:
          // `TableFooterSimple.jsx:64-66`, `FilterSheet.jsx:365-366`.
          borderColor: 'divider',
          ...(sx || {}),
        }}
      >
        {/* Kein `role="status"` — P8 laesst genau EINEN Zaehler je Ansicht zu. */}
        <Typography variant="body2" sx={{ fontSize: FS.sm, fontWeight: 600, flexShrink: 0 }}>
          {umfangText}
        </Typography>

        <Box sx={{ flex: 1 }} />

        {actions.map((a) => (
          <Button
            key={a.label}
            size="small"
            variant="outlined"
            onClick={a.onClick}
            disabled={Boolean(a.disabled)}
            startIcon={a.icon}
          >
            {a.label}
          </Button>
        ))}

        {more.length > 0 ? <RowActionsMenu actions={more} label={moreLabel} /> : null}

        {/* 🔴 In Stufe 2 gibt es diesen Link NICHT (R16 B2): er ist der Eintritt in
            Stufe 2, nicht ihr Bedienelement. Stand er dort weiter, holte ein Klick
            die eben abgewaehlten Zeilen kommentarlos zurueck. */}
        {!stufe2 && selection.canSelectAllMatching ? (
          <Button size="small" onClick={selection.selectAllMatching}>
            {stufe2Text}
          </Button>
        ) : null}

        <Button size="small" onClick={selection.clear}>
          {mobil ? 'Cancel' : 'Clear selection'}
        </Button>
      </Box>
    </>
  )
}
